import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Archive, RotateCcw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  verified: 'bg-blue-100 text-blue-800',
  published: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-gray-200 text-gray-600',
};

export default function AdminStories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stories/admin/all`, { withCredentials: true });
      setItems(data.stories || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      await axios.patch(`${API}/stories/admin/${id}/status`, { status }, { withCredentials: true });
      await load();
    } catch { /* noop */ }
    setBusy('');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading story submissions…</p>;
  if (items.length === 0) return <p className="text-sm text-gray-500">No story submissions yet.</p>;

  return (
    <div className="space-y-3" data-testid="admin-stories-list">
      {items.map((s) => (
        <div key={s.id} className="border border-gray-200 rounded-2xl p-4 bg-white" data-testid={`admin-story-${s.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-gray-900 text-sm">{s.full_name}</span>
              {s.email && <span className="text-xs text-gray-500">{s.email}</span>}
              {s.rating ? <span className="text-[11px] font-bold text-amber-600">{'★'.repeat(s.rating)}{'☆'.repeat(5 - s.rating)}</span> : null}
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{s.role}</span>
              {s.stage && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.stage}</span>}
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${BADGE[s.status] || BADGE.pending}`}>{s.status}</span>
            </div>
            <div className="flex gap-2">
              {s.status !== 'published' && (
                <button onClick={() => setStatus(s.id, 'published')} disabled={!!busy}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1.5"
                  data-testid={`admin-story-publish-${s.id}`}>
                  {busy === s.id + 'published' ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Publish
                </button>
              )}
              {s.status === 'published' && (
                <button onClick={() => setStatus(s.id, 'pending')} disabled={!!busy}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5"
                  data-testid={`admin-story-unpublish-${s.id}`}>
                  <RotateCcw size={11} /> Unpublish
                </button>
              )}
              {s.status !== 'archived' && (
                <button onClick={() => setStatus(s.id, 'archived')} disabled={!!busy}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5"
                  data-testid={`admin-story-archive-${s.id}`}>
                  {busy === s.id + 'archived' ? <Loader2 size={11} className="animate-spin" /> : <Archive size={11} />} Remove
                </button>
              )}
            </div>
          </div>
          {s.before && <p className="text-xs text-gray-600"><strong>Before:</strong> {s.before}</p>}
          {s.after && <p className="text-xs text-emerald-700"><strong>Now:</strong> {s.after}</p>}
          <p className="text-sm text-gray-700 mt-1 leading-relaxed">{s.story}</p>
          {s.location && <p className="text-[11px] text-gray-400 mt-1">{s.location}</p>}
        </div>
      ))}
    </div>
  );
}
